import React from 'react';
import { ContractorStatsEntity } from '../../../../domain/stats';
import moment from 'moment';

import { StatsFilters } from '../../../../domain/statsFilters';
import { SupportedCurrency } from '../../../../domain/currency';
import { useFindAllUsersRepository } from '../../../../repository/users/useFindAllUsersRepository';

interface ExportFiltersSummaryProps {
    filters: StatsFilters;
    selectedCurrency: SupportedCurrency;
    selectedContractor?: ContractorStatsEntity;
}

export const ExportFiltersSummary: React.FC<ExportFiltersSummaryProps> = ({
    filters,
    selectedCurrency,
    selectedContractor,
}) => {
    const { data: usersAccounts = [] } = useFindAllUsersRepository();

    // Add this helper function
    const numberToRoman = (quarterNumber: number): string => {
        switch (quarterNumber) {
            case 1: return 'I';
            case 2: return 'II';
            case 3: return 'III';
            case 4: return 'IV';
            default: return String(quarterNumber);
        }
    };

    // Format time period filter
    const getTimePeriodText = () => {
        const { timePeriodSelector, singleMonthYear, monthYearRange, quarter, halfYear, yearFilter } = filters;
        
        if (timePeriodSelector === 'single-month' && singleMonthYear) {
            const date = new Date(singleMonthYear.year, singleMonthYear.month - 1);
            return <>Miesiąc: <b>{moment(date).format('MM-YYYY')}</b></>;
        }

        if (timePeriodSelector === 'month-range' && monthYearRange) {
            const fromDate = new Date(monthYearRange.fromYear, monthYearRange.fromMonth - 1);
            const toDate = new Date(monthYearRange.toYear, monthYearRange.toMonth - 1);
            return <>Zakres: <b>{moment(fromDate).format('MM-YYYY')} - {moment(toDate).format('MM-YYYY')}</b></>;
        }
        
        if (timePeriodSelector === 'quarter' && quarter) {
            return <>Kwartał: <b>{numberToRoman(quarter.quarter)} {quarter.year}</b></>;
        }
        
        if (timePeriodSelector === 'half-year' && halfYear) {
            return <>Półrocze: <b>{numberToRoman(halfYear.halfYear)} {halfYear.year}</b></>;
        }
        
        if (timePeriodSelector === 'year' && yearFilter) {
            return <>Rok: <b>{yearFilter.year}</b></>;
        }
        
        return <>Brak wybranego okresu</>
    };

    // Format creator filter
    const getCreatorText = () => {
        const creator = filters.creator?.id && usersAccounts.find(user => user.sub === filters.creator?.id);

        if (creator) {
            return <>Spedytor: <b>{creator.firstName} {creator.lastname}</b></>;
        }
        return null;
    };

    // Format contractor filter
    const getContractorText = () => {
        if (selectedContractor) {
            return <>Podwykonawca: <b>{selectedContractor.name}</b></>;
        }
        return null;
    };

    // Get currency text
    const getCurrencyText = () => {
        return <>Waluta: <b>{selectedCurrency}</b></>;
    };

    // Collect all filter texts
    const filterTexts = [
        getTimePeriodText(),
        getCreatorText(),
        getContractorText(),
        getCurrencyText(),
    ];

    // Filter out null values and join with line breaks
    const filtersText = filterTexts.filter(Boolean);

    return (
        <div className="export-filters-summary mb-3">
            <h6>Wybrane filtry:</h6>
            <div className="p-2 bg-light rounded border">
                {filtersText.map((line, index) => (
                    <div key={index} className="mb-1">
                        {line}
                    </div>
                ))}
            </div>
        </div>
    );
};